import { TheWestSection } from '@news-mono/common'
import {
    PublicationType,
    BreachEvent,
    useFeature,
    CardEvents,
    CollectionEvent,
    LinkClickedEvent,
    PlayerEvent,
    Block,
} from '@news-mono/web-common'
import {
    RequiredAccessLevel,
    TheWestSections,
} from '@west-australian-newspapers/publication-types'
import React, { useState, useEffect } from 'react'
import { useSection } from '../../../../__helpers'
import { MessageBanner } from '../../../../banners/MessageBanner/MessageBanner'
import { MessageBannerSmall } from '../../../../banners/MessageBannerSmall/MessageBannerSmall'
import {
    useFeatureForRegionalSections,
    useFeatureForSection,
} from '../../../../feature-toggling'
import {
    useActiveExperiment,
    useVariantId,
    AbTestExperiment,
} from '../../abtesting/AbTestingProvider'
import { useUpdateBreachWallContext, packageOfferDetails } from '../../common'
import {
    BreachWallStyle,
    BreachWallContentType,
} from '../../helpers/BreachWallTypes'
import { WestRewardsMessage } from './WestRewardsMessage/WestRewardsMessage'
import BreachWallContent from './BreachWallContent'
import {
    StyledTitle,
    StyledMessage,
    StyledTerms,
    StyledABTestBannerWrap,
    StyledLiveTrailerWrap,
    StyledTitleImage,
    StyledBlackFridayTape,
} from './BreachWallContent.styled'
import { VideoTrailerCard } from '../../../../cards/VideoTrailerCard/VideoTrailerCard'
import { getBreachWallOptions } from '../../helpers'
import { ArticlePreview } from './ArticlePreview/ArticlePreview'
import { Logger } from 'typescript-log'
import H from 'history'
import debug from 'debug'

const breachWallDebug = debug('breach-wall')

export const defaultBannerText =
    'Subscribers with digital access can view this article.'

const defaultCreative = 'TW_Article_Breach_WestBlue_Design_PhotoPackages'
const defaultStyle: BreachWallStyle = 'default-breachwall'
const defaultType: BreachWallContentType = 'default'
const defaultDeal = ''

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscount50afl: boolean
    canShowBreachDiscount50sch: boolean
    canShowBreachDiscount190Year: boolean
    canShowBlackFriday: boolean
    canShowBreachDiscountGeneric: boolean
    canShowEofyDiscount: boolean
    canShowBusinessPackDiscount: boolean
    canShowSportPackDiscount: boolean
    canShowBaliBombingDiscount: boolean
}

export type BannerSize = 'small' | 'large'

export interface BreachWallContentWrapperProps {
    // Supplied events to utilise, else if not provided it'll utilise defaults
    suppliedDeal?: string
    suppliedType?: BreachWallContentType
    suppliedCreative?: string
    suppliedStyle?: BreachWallStyle
    // Supplied AB testing parameters, else utilise new parameters
    suppliedExperiment?: AbTestExperiment
    // Publication data to update breach wall context
    publication?: PublicationType
    requiredAccess?: RequiredAccessLevel
    location: H.Location
    // Consume all events
    onEvent: (
        event:
            | BreachEvent
            | PlayerEvent
            | LinkClickedEvent
            | CardEvents
            | CollectionEvent,
    ) => void
    // Values to set the top "banner" values
    bannerSize?: BannerSize
    bannerText?: string
    // Overriding values used from within the breach screen itself
    title?: string
    message?: string
    terms?: string
    log: Logger
    blocks?: Block[]
    forceLoad?: boolean
    isTest?: boolean
    // Optional properties that can be passed on a per-campaign basis
    contentOptions?: {
        [key: string]: any
    }
}

export const BreachWallContentWrapper: React.FC<BreachWallContentWrapperProps> =
    ({
        suppliedDeal,
        suppliedType,
        suppliedCreative,
        suppliedStyle,
        suppliedExperiment,
        publication,
        requiredAccess,
        onEvent,
        bannerSize,
        bannerText,
        title,
        message,
        terms,
        log,
        blocks,
        forceLoad,
        isTest,
        contentOptions,
        location,
    }) => {
        const section = useSection<TheWestSection>()
        const isRegionalOverride = useFeatureForRegionalSections(
            'breach-regional-override',
            section,
        )
        const isNewDesign2022 =
            useFeatureForSection({
                feature: 'tp-subscription-new-breach-package-background',
                sections: TheWestSections.default,
            }) ||
            isTest === true ||
            isRegionalOverride
        const showPartOfArticle = useFeatureForSection({
            feature: 'tp-subscription-breach-show-part-article',
            sections: TheWestSections.default,
        })

        const canShowAbTests = useFeature('breach-ab-testing')
        const otherBreachOptions = useBreachWallOptions()

        const [isLoading, setLoading] = useState(
            forceLoad === undefined ? true : !forceLoad,
        )

        const shouldShowLiveSportsTrailer =
            contentOptions && contentOptions.showLiveSportTrailer

        const [breachDeal, setBreachDeal] = useState<string>(
            suppliedDeal || defaultDeal,
        )
        const [breachType, setBreachType] = useState<BreachWallContentType>(
            suppliedType || defaultType,
        )
        const [breachStyle, setBreachStyle] = useState<BreachWallStyle>(
            suppliedStyle || defaultStyle,
        )
        const [isAbTest, setAbTest] = useState<boolean>(
            canShowAbTests && suppliedExperiment !== undefined,
        )
        const breachCreative = suppliedCreative || defaultCreative

        const activeExperiment = useActiveExperiment(
            section,
            location,
            canShowAbTests,
        )
        const variant = useVariantId(activeExperiment)

        useEffect(() => {
            // force load is used and so it's probably coming from a test
            // so we don't want it to do any calculation, as it should be
            // supplied with everything it needs
            if (forceLoad === true) {
                return
            }
            // if it's not utilising the new tp breach designs, we don't want to try
            // and load a custom breach screen styling over it.
            if (!isNewDesign2022) {
                setLoading(false)
                return
            }

            // if showing ab tests and one is found for this location, utilise that
            if (isNewDesign2022 && canShowAbTests) {
                if (
                    typeof activeExperiment !== 'undefined' &&
                    variant !== undefined
                ) {
                    const breachWall =
                        activeExperiment.getBreachWallConfig(variant)

                    setBreachDeal(breachWall.deal)
                    setBreachType(breachWall.type)
                    setBreachStyle(activeExperiment.getBreachStyle(variant))
                    setLoading(false)
                    setAbTest(true)
                    return
                }
            }

            // an ab test experiment wasn't found, so now we will check against other breach types
            const otherBreachWall = getBreachWallOptions(
                location,
                otherBreachOptions,
            )
            if (otherBreachWall !== undefined) {
                setBreachDeal(otherBreachWall.deal)
                setBreachType(otherBreachWall.type)
                setBreachStyle(otherBreachWall.style)
                setAbTest(false)
            }

            setLoading(false)
        }, [
            canShowAbTests,
            forceLoad,
            isLoading,
            activeExperiment,
            variant,
            location,
            otherBreachOptions,
            isNewDesign2022,
        ])

        const experiment =
            suppliedExperiment !== undefined
                ? suppliedExperiment
                : activeExperiment

        // fire breach wall context update
        useUpdateBreachWallContext({
            publication: publication,
            requiredAccessLevel: requiredAccess || 'subscriber',
            breachDeal: breachDeal,
            breachCreativeName: breachCreative,
        })

        const subscriptionPackages = packageOfferDetails(
            breachDeal === 'epaper-regional'
                ? 'digital-only'
                : breachType
                ? breachType
                : 'default',
        )

        // some logging so that we have relevant information on the breach screen available.
        if (!isLoading || forceLoad) {
            breachWallDebug(
                'Breach screen type has been determined, now about to generate a BreachWallContent with the following values:',
            )
            breachWallDebug('ContentType:', breachType)
            breachWallDebug('Style:', breachStyle)
            breachWallDebug('Deal:', breachDeal)
            breachWallDebug('IsAbTest:', isAbTest)
        }

        return (
            <>
                {showPartOfArticle && publication && (
                    <ArticlePreview
                        blocks={blocks || []}
                        numberOfBlocksToPreview={2}
                        log={log}
                        onEvent={onEvent}
                    />
                )}
                <div data-testid="BreachScreen">
                    {getBreachBanner(
                        bannerText || defaultBannerText,
                        bannerSize || 'small',
                        onEvent,
                    )}
                    {!isLoading && (
                        <BreachWallContent
                            breachCreativeName={breachCreative}
                            breachType={breachType}
                            breachDeal={breachDeal}
                            breachStyle={breachStyle}
                            isTest={isTest}
                            onEvent={onEvent}
                            title={() => {
                                if (
                                    isAbTest &&
                                    experiment !== undefined &&
                                    variant !== undefined
                                ) {
                                    return getAbTestTitle(
                                        breachStyle,
                                        experiment,
                                        variant,
                                    )
                                } else if (breachStyle === 'black-friday') {
                                    return (
                                        <StyledTitleImage
                                            src={getTitleBannerImage(
                                                breachStyle,
                                            )}
                                        />
                                    )
                                } else {
                                    return (
                                        <React.Fragment>
                                            {title && (
                                                <StyledTitle>
                                                    {title}
                                                </StyledTitle>
                                            )}
                                            {message && (
                                                <StyledMessage>
                                                    {message}
                                                </StyledMessage>
                                            )}
                                        </React.Fragment>
                                    )
                                }
                            }}
                            terms={() => {
                                if (terms) {
                                    return <StyledTerms>{terms}</StyledTerms>
                                }

                                return null
                            }}
                            mediaBanner={() => {
                                if (shouldShowLiveSportsTrailer) {
                                    return (
                                        <StyledLiveTrailerWrap>
                                            <VideoTrailerCard
                                                layoutMode="double"
                                                onEvent={() => null}
                                            />
                                        </StyledLiveTrailerWrap>
                                    )
                                }
                                if (breachStyle === 'black-friday') {
                                    const tape = require('./assets/thewest-tape-black-friday-1.png')

                                    return (
                                        <>
                                            <StyledBlackFridayTape
                                                src={tape}
                                                breachStyle={breachStyle}
                                                className="left-tape"
                                            />
                                            <StyledBlackFridayTape
                                                src={tape}
                                                breachStyle={breachStyle}
                                                className="right-tape"
                                            />
                                            <StyledBlackFridayTape
                                                src={tape}
                                                breachStyle={breachStyle}
                                                className="bottom-tape"
                                            />
                                        </>
                                    )
                                }

                                return null
                            }}
                            subscriptionPackages={subscriptionPackages}
                            abTestExperiment={experiment}
                            abTestVariant={variant}
                            isRegionalOverride={isRegionalOverride}
                        />
                    )}
                    {isNewDesign2022 && <WestRewardsMessage />}
                </div>
            </>
        )
    }

function getAbTestTitle(
    style: BreachWallStyle,
    experiment: AbTestExperiment,
    variant: number,
): JSX.Element {
    const bannerLayout = experiment.getBreachBannerConfig(variant)
    const topBannerText = bannerLayout?.topBannerText
    const splitText = topBannerText?.split(' ')

    return (
        <React.Fragment>
            <StyledABTestBannerWrap breachStyle={style}>
                <StyledMessage breachStyle={style}>
                    {style === 'vanishing-cousins' &&
                    typeof splitText !== 'undefined' ? (
                        <p>
                            {splitText.shift()}
                            <b> {splitText.shift()} </b>
                            {splitText.join(' ')}
                        </p>
                    ) : (
                        <p>{topBannerText}</p>
                    )}
                    <StyledTitle>{bannerLayout?.mainBannerText}</StyledTitle>
                    <p className="full-width">{bannerLayout?.subBannerText}</p>
                    {style !== 'vanishing-cousins' && (
                        <p className="abtest-terms">
                            {bannerLayout?.tncBannerText}
                        </p>
                    )}
                </StyledMessage>
            </StyledABTestBannerWrap>
        </React.Fragment>
    )
}

function getBreachBanner(
    bannerText: string,
    bannerSize: BannerSize,
    onEvent: (event: BreachEvent) => void,
): JSX.Element {
    return bannerSize === 'large' ? (
        <MessageBanner
            icon="subscriber"
            connector="solid-arrow"
            onEvent={onEvent}
        >
            <p>{bannerText}</p>
        </MessageBanner>
    ) : (
        <MessageBannerSmall icon="subscriber" onEvent={onEvent}>
            <p>{bannerText}</p>
        </MessageBannerSmall>
    )
}

function getTitleBannerImage(breachStyle: BreachWallStyle): string | undefined {
    switch (breachStyle) {
        case 'black-friday':
            return require('./assets/thewest-banner-black-friday.png')
    }
}

function useBreachWallOptions(): BreachWallOptions {
    return {
        canShowSportBreach: useFeature('sport-breach-screen-variant'),
        canShowBreachDiscount50sch: useFeature('top-50-schools'),
        canShowBreachDiscount190Year: useFeature('breach-190-year'),
        canShowBlackFriday: useFeature('black-friday'),
        canShowBreachDiscountGeneric: useFeature('50-off-breach-discount'),
        canShowEofyDiscount: useFeature('subs-campaign-eofy-weekend-digital'),
        canShowBusinessPackDiscount: useFeature(
            'subs-campaign-business-pack-2022',
        ),
        canShowSportPackDiscount: useFeature('subs-campaign-sport-pack-2022'),
        canShowBaliBombingDiscount: useFeature(
            'subs-campaign-bali-bombing-2022',
        ),
        canShowBreachDiscount50afl: useFeature('50-off-afl-breach-screen'),
    }
}

BreachWallContentWrapper.displayName = 'BreachWallContentWrapper'

export default BreachWallContentWrapper
